import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BellOff, CalendarCheck, Check, LayoutGrid, Plus, Sparkles } from 'lucide-react';
import type { HomeLayout, HomePayload, HomeSectionId } from '@unitly/contracts';
import { applyWorkspaceShelf, DEFAULT_HOME_LAYOUT, HOME_SECTION_DESCRIPTIONS, HOME_SECTION_LABELS } from '@unitly/contracts';
import { useHome, useHomeLayout, useMarkRead, useNotifications, useSetHomeLayout, useWorkspaceShelf } from '../api/queries.ts';
import { useOrgCtx } from '../app/org-context.tsx';
import { PageHeader } from '../features/shell/Chrome.tsx';
import { TaskRow } from '../features/tasks/TaskRow.tsx';
import { NotificationIcon } from '../features/notifications/NotificationIcon.tsx';
import { HomeGrid } from '../features/home/HomeGrid.tsx';
import { useToast } from '../ui/Toast.tsx';
import { Avatar, BrandLogo, EmptyState, WorkspaceAvatar } from '../ui/primitives.tsx';
import { greeting, longDate, relativeTime } from '../lib/dates.ts';

function summary(h: HomePayload): string {
  const { today, overdue, upcoming } = h.counts;
  const parts: string[] = [];
  if (today) parts.push(today === 1 ? '1 task per oggi' : `${today} task per oggi`);
  if (overdue) parts.push(overdue === 1 ? '1 in ritardo' : `${overdue} in ritardo`);
  if (!parts.length) return upcoming ? `Oggi sei libero. ${upcoming} in arrivo nei prossimi giorni.` : 'Niente in scadenza. Buon lavoro.';
  return `Hai ${parts.join(' e ')}.`;
}

function Skeleton() {
  return (
    <div className="page">
      <div style={{ height: 120 }} />
      <div className="skeleton" style={{ height: 70, marginBottom: 24 }} />
      <div className="skeleton" style={{ height: 180, marginBottom: 16 }} />
      <div className="skeleton" style={{ height: 120 }} />
    </div>
  );
}

const ACTIVITY_PREVIEW = 6;

export function HomePage() {
  const { orgId, base, today, detail, openQuickAdd, openCreateWorkspace } = useOrgCtx();
  const home = useHome(orgId);
  const layoutQuery = useHomeLayout(orgId);
  const setLayout = useSetHomeLayout(orgId);
  const shelf = useWorkspaceShelf(orgId);
  const toast = useToast();
  const [allActivity, setAllActivity] = useState(false);
  /** Unsaved arrangement while editing; null when not editing. */
  const [draft, setDraft] = useState<HomeLayout | null>(null);
  if (home.isLoading || !home.data) return home.error ? <div className="page"><EmptyState tone="neutral" title="Home non disponibile">{home.error.message}</EmptyState></div> : <Skeleton />;
  const h = { ...home.data, workspaces: shelf.data ? applyWorkspaceShelf(home.data.workspaces, shelf.data) : home.data.workspaces };
  const saved: HomeLayout = layoutQuery.data ?? DEFAULT_HOME_LAYOUT;
  const layout = draft ?? saved;
  const editing = draft !== null;
  const anyVisible = layout.sections.some((s) => s.visible);
  const startEditing = () => setDraft(saved);
  const finishEditing = () => {
    if (!draft) return;
    setLayout.mutate(draft, { onSuccess: () => { setDraft(null); toast('Home aggiornata'); }, onError: () => toast('Non è stato possibile salvare') });
  };
  const nothingDue = !h.today.length && !h.overdue.length;

  const sections: Record<HomeSectionId, React.ReactNode> = {
    stories: (
      <nav className="stories" aria-label="Spazi" key="stories">
        {h.workspaces.map((w) => (
          <Link key={w.id} to={`${base}/w/${w.key}`} className="story" aria-label={`${w.name}${w.myAttentionTasks ? `, ${w.myAttentionTasks} da fare oggi` : ''}`}>
            <WorkspaceAvatar name={w.name} keyLabel={w.key} color={w.color} logoUrl={w.logoUrl} active={w.myAttentionTasks > 0} />
            <span className="story-label">{w.name}</span>
          </Link>
        ))}
        {detail?.capabilities.createWorkspace && (
          <button type="button" className="story story-add" onClick={openCreateWorkspace} aria-label="Nuovo spazio">
            <span className="story-ring"><span className="story-inner"><span className="story-avatar"><Plus size={24} /></span></span></span>
            <span className="story-label">Nuovo</span>
          </button>
        )}
      </nav>
    ),
    overdue: h.overdue.length > 0 ? (
      <section className="section" key="overdue">
        <div className="section-head"><h2 className="section-title danger">In ritardo <span className="n">{h.overdue.length}</span></h2></div>
        <div className="card list">{h.overdue.map((t) => <TaskRow key={t.id} task={t} />)}</div>
      </section>
    ) : null,
    today: (
      <section className="section" key="today">
        <div className="section-head">
          <h2 className="section-title">Oggi <span className="n">{h.today.length || ''}</span></h2>
          <button type="button" className="btn btn-plain btn-sm" onClick={() => openQuickAdd({ dueDate: today })}><Plus size={16} /> Aggiungi</button>
        </div>
        <div className="card list">
          {h.today.map((t) => <TaskRow key={t.id} task={t} />)}
          {nothingDue && (
            <EmptyState title="Tutto fatto per oggi">
              {h.upcoming.length ? 'Puoi portarti avanti con le task dei prossimi giorni.' : 'Crea una task con il pulsante ＋, oppure scrivi “domani alle 10” per programmarla.'}
            </EmptyState>
          )}
          {!nothingDue && !h.today.length && <p className="hint" style={{ padding: '14px 16px', margin: 0 }}>Niente in scadenza oggi oltre a quelle in ritardo.</p>}
        </div>
      </section>
    ),
    upcoming: h.upcoming.length > 0 ? (
      <section className="section" key="upcoming">
        <div className="section-head">
          <h2 className="section-title">Prossimi 7 giorni <span className="n">{h.upcoming.length}</span></h2>
          <Link className="btn btn-plain btn-sm" to={`${base}/tasks`}>Tutte</Link>
        </div>
        <div className="card list">{h.upcoming.map((t) => <TaskRow key={t.id} task={t} />)}</div>
      </section>
    ) : null,
    activity: (
      <section className="section" key="activity">
        <div className="section-head"><h2 className="section-title">Attività del team</h2></div>
        <div className="card activity list">
          {h.activity.length === 0 && (
            <EmptyState tone="neutral" icon={<Sparkles size={20} />} title="Ancora silenzio">
              Qui vedrai cosa succede: task create e completate, commenti, nuovi spazi e persone.
            </EmptyState>
          )}
          {(allActivity ? h.activity : h.activity.slice(0, ACTIVITY_PREVIEW)).map((a) => (
            <div className="activity-item" key={a.id}>
              {a.actor ? <Avatar id={a.actor.id} name={a.actor.displayName} size={30} /> : <span className="avatar" style={{ '--size': '30px' } as React.CSSProperties}><CalendarCheck size={14} /></span>}
              <div>
                <div className="text">{a.text}</div>
                <time dateTime={a.occurredAt}>{relativeTime(a.occurredAt)}</time>
              </div>
            </div>
          ))}
          {/* A long feed made the right column run far below the left one, leaving a big empty area. */}
          {h.activity.length > ACTIVITY_PREVIEW && (
            <button type="button" className="list-row list-more" onClick={() => setAllActivity((v) => !v)}>
              {allActivity ? 'Mostra meno' : `Mostra altre ${h.activity.length - ACTIVITY_PREVIEW}`}
            </button>
          )}
        </div>
      </section>
    ),
    notifications: <NotificationsWidget key="notifications" />,
  };

  const editButton = (size: number) => editing
    ? <button type="button" className="btn btn-primary btn-sm" disabled={setLayout.isPending} onClick={finishEditing}><Check size={16} /> Fine</button>
    : <button type="button" className="icon-btn" aria-label="Modifica Home" title="Modifica Home" onClick={startEditing}><LayoutGrid size={size} /></button>;

  return (
    <div className="page wide">
      <div className="home-topbar desktop-only">
        <BrandLogo height={20} />
        <span className="spacer" />
        {editButton(18)}
      </div>
      <PageHeader
        title={`${greeting()}, ${h.firstName}`}
        subtitle={`${longDate(today)} · ${summary(h)}`}
        actions={<button type="button" className="btn btn-primary btn-sm" onClick={() => openQuickAdd({ dueDate: today })}><Plus size={16} /> Task per oggi</button>}
        mobileActions={editButton(20)}
      />

      {editing && (
        <div className="home-editbar">
          <p className="hint">Trascina le sezioni dalla maniglia, scegli quante colonne occupano e nascondi quelle che non ti servono.</p>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDraft(null)}>Annulla</button>
        </div>
      )}

      <HomeGrid
        layout={layout}
        editing={editing}
        onChange={setDraft}
        render={(id) => sections[id]}
        placeholder={(id) => (
          <section className="section">
            <div className="section-head"><h2 className="section-title">{HOME_SECTION_LABELS[id]}</h2></div>
            <div className="card"><p className="hint home-placeholder">{HOME_SECTION_DESCRIPTIONS[id]}. Ora è vuota, comparirà quando ci sarà qualcosa.</p></div>
          </section>
        )}
      />

      {!anyVisible && (
        <div className="card"><EmptyState tone="neutral" icon={<LayoutGrid size={20} />} title="Home vuota">
          Hai nascosto tutte le sezioni.{' '}
          {!editing && <button type="button" className="btn btn-plain btn-sm" onClick={startEditing} style={{ display: 'inline-flex' }}>Modifica Home</button>}
        </EmptyState></div>
      )}
    </div>
  );
}

function NotificationsWidget() {
  const { orgId, base, openTask, openContent } = useOrgCtx();
  const notifications = useNotifications(orgId);
  const markRead = useMarkRead(orgId);
  const items = (notifications.data?.items ?? []).slice(0, 5);
  return (
    <section className="section">
      <div className="section-head"><h2 className="section-title">Notifiche recenti</h2></div>
      <div className="card list">
        {items.length === 0 && (
          <EmptyState tone="neutral" icon={<BellOff size={20} />} title="Nessuna notifica">Le assegnazioni e i commenti che ti riguardano compaiono qui.</EmptyState>
        )}
        {items.map((n) => (
          <button
            key={n.id}
            type="button"
            className={`inbox-item${n.readAt ? '' : ' unread'}`}
            onClick={() => {
              if (!n.readAt) markRead.mutate({ ids: [n.id] });
              if (n.entityType === 'task' && n.entityId) openTask(n.entityId);
              if (n.entityType === 'content_item' && n.entityId) openContent(n.entityId);
            }}
          >
            <NotificationIcon n={n} size={32} />
            <span className="grow">
              <span className="title">{n.title}</span>
              {n.body && <span className="body">{n.body}</span>}
            </span>
            <time dateTime={n.createdAt}>{relativeTime(n.createdAt)}</time>
          </button>
        ))}
        {(notifications.data?.items.length ?? 0) > items.length && (
          <Link to={`${base}/inbox`} className="list-row list-more">Vedi tutte</Link>
        )}
      </div>
    </section>
  );
}
